import { useListContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { List } from "@/components/admin/list";
import { InfinitePagination } from "../misc/InfinitePagination";

import { MobileListPage } from "../layout/MobileListPage";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";

/**
 * Leads on a phone, grouped by stage.
 *
 * The desktop screen is a Kanban board: columns of cards dragged between
 * stages, which needs width and a pointer. Dragging on a phone is the wrong
 * interaction anyway -- it competes with scrolling and has no room to show a
 * target. So the stages become sections you scroll through, and moving a lead
 * happens on the lead itself, where there is space to say what the stages are.
 *
 * Same information, same order, without pretending a board fits.
 */
const DealsByStage = () => {
  const { data, isPending } = useListContext<Deal>();
  const { dealStages, currency } = useConfigurationContext();

  if (isPending) return null;
  const deals = data ?? [];

  const money = (amount: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <div className="flex flex-col gap-5">
      {dealStages.map((stage) => {
        const inStage = deals.filter((deal) => deal.stage === stage.value);
        if (inStage.length === 0) return null;

        return (
          <section key={stage.value} className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold">{stage.label}</h2>
              <span className="text-xs text-muted-foreground">
                {inStage.length}
              </span>
            </div>

            <div className="space-y-2">
              {inStage.map((deal) => (
                <Link
                  key={deal.id}
                  to={`/deals/${deal.id}/show`}
                  className="block rounded-lg border p-3 active:bg-accent/50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-sm">{deal.name}</span>
                    {deal.amount ? (
                      <span className="text-sm tabular-nums shrink-0">
                        {money(deal.amount)}
                      </span>
                    ) : null}
                  </div>
                  {deal.category ? (
                    <Badge variant="outline" className="mt-1.5">
                      {deal.category}
                    </Badge>
                  ) : null}
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      {deals.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No leads yet.
        </p>
      ) : null}
    </div>
  );
};

export const MobileDealList = () => {
  const translate = useTranslate();
  return (
    <List
      actions={false}
      title={false}
      sort={{ field: "index", order: "ASC" }}
      filter={{ "archived_at@is": null }}
      perPage={100}
      pagination={false}
    >
      <MobileListPage
        searchPlaceholder={translate("resources.deals.name", {
          smart_count: 2,
        })}
      >
        <DealsByStage />
        <div className="flex justify-center pt-4">
          <InfinitePagination />
        </div>
      </MobileListPage>
    </List>
  );
};

export default MobileDealList;
